import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import useNumberOfUnseenItems from 'components/Dashboard/hooks/useNumberOfUnseenItems';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { store } from 'components/Store';
import { useContext, useEffect, useRef, useState } from 'react';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import { AgencyImage, OrganizationImage } from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioElements';
import { useAuth0 } from '@auth0/auth0-react';

const MarkAsReadButton = () => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { team, activePlan } = state;
  const { setFeedState } = useContentResults();
  const topicParam = getUrlParam('topic-id');
  const { newAgencyPlan } = useTopicOrClientWord();
  const [showActions, setShowActions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [position, setPosition] = useState({ top: '0px', left: '0px', opacity: 0 });
  const actionsContainer = useRef(null);
  const buttonElement = useRef(null);
  const { user } = useAuth0();
  const clientTypeWord = team?.expires ? 'prospect' : 'client';

  const hideItem = () => {
    setShowActions(false);
  };

  useOutsideElement(actionsContainer, hideItem);
  const { generalApiCall } = useGeneralApiCall();
  const { callUnseenItemsList } = useNumberOfUnseenItems();

  const markAsReadFunction = async ({ allClients = false } = {}) => {
    if (!!topicParam) {
      try {
        setIsLoading(true);
        const pathname = allClients
          ? '/api/feed/feed-last-seen-markers/all-teams'
          : `/api/feed/feed-last-seen-markers${topicParam === 'all' ? '' : `?keywordListId=${topicParam}`}`;
        const method = 'delete';
        const results = await generalApiCall({
          pathname,
          method,
          needsAuthentication: true,
          returnCompleteRequest: true,
        });
        if (results?.status === 200) {
          setFeedState('lastSeenMarkers', results?.data);
          createNotification('success', 'All updates marked as read');
          setShowActions(false);

          const listId = topicParam === 'all' ? null : parseInt(topicParam);
          let numbersCurrentList = await callUnseenItemsList({ listId });
          if (numbersCurrentList) {
            dispatch({
              type: 'MODIFY_SECTION',
              parameter: 'unseenNumbersCurrentList',
              value: Object.keys(numbersCurrentList).length > 0 ? numbersCurrentList : {},
            });
          }
        }
        setIsLoading(false);
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (showActions && !!buttonElement?.current) {
      const { bottom, left } = buttonElement?.current?.getBoundingClientRect();
      setPosition({ top: `${bottom + 3}px`, left: `${left - 50}px`, opacity: 1 });
    }
  }, [showActions]);

  const { inboxSection } = useMainSectionParameter();

  return (
    <div
      className='position-relative ml-1'
      style={{ visibility: inboxSection ? 'visible' : 'hidden' }} //AE: Using visibility to avoid jumping effects between sections
      ref={actionsContainer}
    >
      <button
        className={`general-button circle-button hover-background-gray-05 d-inline-block-centered specific-top-margin-4`}
        onClick={(e) => {
          e.stopPropagation();
          setShowActions(!showActions);
        }}
        ref={buttonElement}
      >
        <span className='icon-three-dots light-40-text' />
      </button>
      {showActions && (
        <div
          style={{ ...position, minWidth: 'max-content' }}
          className='box-shadow rounded py-2 position-fixed z-extra-max'
        >
          {newAgencyPlan ? (
            <>
              <button
                className={
                  'list-action-button w-100 general-button text-left specific-y-padding-10 specific-x-padding-24 flex-centered gap-10'
                }
                disabled={isLoading}
                onClick={() => markAsReadFunction()}
              >
                {isLoading ? (
                  <i className='fas fa-spinner fa-spin d-inline-block-centered mr-2' />
                ) : (
                  <AgencyImage team={team} />
                )}
                <span>
                  Mark all my updates for <span className='font-weight-bold'>this {clientTypeWord}</span> as read
                </span>
              </button>
              <button
                className={
                  'list-action-button w-100 general-button text-left specific-y-padding-10 specific-x-padding-24 flex-centered gap-10'
                }
                disabled={isLoading}
                onClick={() => markAsReadFunction({ allClients: true })}
              >
                {isLoading ? (
                  <i className='fas fa-spinner fa-spin d-inline-block-centered mr-2' />
                ) : (
                  <OrganizationImage activePlan={activePlan} user={user} />
                )}
                <span>
                  Mark all my updates for <span className='font-weight-bold'>all my clients and prospects</span> as read
                </span>
              </button>
            </>
          ) : (
            <button
              className={
                'list-action-button w-100 d-block general-button text-left specific-y-padding-10 specific-x-padding-24'
              }
              disabled={isLoading}
              onClick={() => markAsReadFunction()}
            >
              {isLoading && <i className='fas fa-spinner fa-spin d-inline-block-centered mr-2' />}Mark all updates as
              read
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MarkAsReadButton;
